import React, { useState } from "react";
import styles from "./PaymentList.module.scss";

import moment from "moment";
import useConfirmPopup from "../../context/useConfirmPopup";
import { PurchasePaymentsProps } from "../../types/purchasePayments";
import { useAppDispatch } from "../../app/hooks";
import {
  setEditPurchasePayment,
  togglePurchaseCreateModal,
} from "../../features/purchasePayments/purchasePaymentsSlice";
import { GET } from "../../services/axios.service";
import { toast } from "react-toastify";
import { toastStyles } from "../../static/styles";

type Props = {
  payments: PurchasePaymentsProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeletePayment: (id: number, password: string) => void;
};

const PaymentList = ({
  payments,
  pageNumber,
  pageSize,
  isLoading,
  handleDeletePayment,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleEditPayment = async (payment: PurchasePaymentsProps) => {
    if (payment.status === 1) {
      await dispatch(setEditPurchasePayment(payment));
      dispatch(togglePurchaseCreateModal(2));
    } else if (payment.status === 2) {
      await dispatch(setEditPurchasePayment(payment));
      dispatch(togglePurchaseCreateModal(3));
    }
  };

  const handleDeleteRow = async (id: number) => {
    const response = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (response.confirmed) {
      setIsActionLoading(id);
      await handleDeletePayment(id, response.password);
      setIsActionLoading(0);
    }
  };

  const handleDownloadPdf = async (purchaseId: number) => {
    try {
      setIsActionLoading(purchaseId);
      const response: any = await GET(`/v1/admin/purchases/${purchaseId}/pdf`, {});
      if (response.data.status === "success") {
        const downloadLink = response.data.data.download_link;
        try {
          const newWindow = window.open(downloadLink, "_blank");
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed == "undefined"
          ) {
            throw new Error("પોપ્અપ બ્લોક કરેલ છે.");
          }
        } catch (e) {
          window.location.href = downloadLink;
        }
      } else {
        toast.error(response.data.message, {
          ...(toastStyles as any),
        });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        {
          ...(toastStyles as any),
        }
      );
    } finally {
      setIsActionLoading(0);
    }
  };

  return (
    <div className={styles.PaymentList}>
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    Sr. No.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    પેમેન્ટ ID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    ટાઈપ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    વિગતો
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-base text-primary-1 font-bold tracking-wider"
                  >
                    અમાઉન્ટ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    તારીખ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-base text-primary-1 font-bold tracking-wider"
                  >
                    એકશન્સ બટન
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-primary-2 font-bold text-xl text-center py-2"
                    >
                      રાહ જુઓ...
                    </td>
                  </tr>
                ) : (
                  <>
                    {payments.length > 0 ? (
                      <>
                        {payments.map((payment, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(pageNumber - 1) * pageSize + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.id || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment?.status === 0
                                ? "ખરીદી"
                                : payment?.status === 1
                                ? "આપેલ પેમેન્ટ"
                                : payment?.status === 2
                                ? "વધ ઘટ"
                                : ""}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment?.status === 0 ? (
                                <>
                                  {payment?.purchases?.map((p, i) => (
                                    <div key={i}>
                                      બિલ નં.: {p.id}
                                      {p.voucher_no && (
                                        <><br />વાઉચર નં: {p.voucher_no}</>
                                      )}
                                      {p.date && (
                                        <><br />તારીખ: {moment(p.date).format("DD-MM-YYYY")}</>
                                      )}
                                      <br />
                                      ટેક્સ: {Number(p.tax).toFixed(2)}
                                      <br />
                                      ડિસ્કાઉન્ટ: {Number(p.discount).toFixed(2)}
                                    </div>
                                  ))}
                                </>
                              ) : payment?.status === 1 ? (
                                payment?.note || ""
                              ) : payment?.status === 2 ? (
                                <>
                                  {payment?.note || ""} -{" "}
                                  {payment?.type === 0
                                    ? "અમાઉન્ટ ઉમેરાયું"
                                    : payment?.type === 1
                                    ? "અમાઉન્ટ બાદ થયું"
                                    : ""}
                                </>
                              ) : (
                                ""
                              )}
                            </td>
                            <td className="px-6 py-4 text-right whitespace-nowrap text-sm text-gray-900">
                              {payment?.amount?.toFixed(2) || "0.00"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {moment(payment.created_at).format(
                                "DD-MM-YYYY hh:mm A"
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {isActionLoading === payment.id ? (
                                <p className="text-primary-2 font-bold text-right text-lg">
                                  રાહ જુઓ...
                                </p>
                              ) : (
                                <>
                                  {payment?.status === 0 && payment?.purchases && payment.purchases.length > 0 && (
                                    <button
                                      onClick={() =>
                                        handleDownloadPdf(payment.purchases![0].id)
                                      }
                                      className="text-indigo-600 hover:text-indigo-900"
                                    >
                                      PDF
                                    </button>
                                  )}
                                  {payment.isEditable && payment.status !== 0 && (
                                    <>
                                      <button
                                        onClick={() => handleEditPayment(payment)}
                                        className="text-indigo-600 hover:text-indigo-900 ml-4"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteRow(payment.id)}
                                        className="text-red-600 hover:text-red-900 ml-4"
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className="text-primary-2 font-bold text-xl text-center py-2"
                        >
                          ખરીદી ના પેમેન્ટ ની વિગતો મળી નથી
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PaymentList;
